import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tabs", "dropdown", "dropdownMenu", "dropdownTemplate", "buildingDisplay"]

  connect() {
    this.checkOverflow()
    window.addEventListener('resize', this.checkOverflow.bind(this))
  }

  disconnect() {
    window.removeEventListener('resize', this.checkOverflow.bind(this))
  }

  checkOverflow() {
    if (this.hasTabsTarget) {
      const tabs = this.tabsTarget
      const wrapper = this.element.querySelector('.nav-tabs-wrapper') || this.element
      const dropdownSpace = 60 // Space reserved for dropdown button
      const availableWidth = wrapper.clientWidth - dropdownSpace
      
      // Get all tab items
      const tabItems = Array.from(tabs.querySelectorAll('.nav-item'))
      
      if (tabItems.length === 0) return
      
      // Calculate how many tabs fit
      let visibleTabs = 0
      let totalWidth = 0
      
      for (let i = 0; i < tabItems.length; i++) {
        const tabWidth = tabItems[i].offsetWidth
        if (totalWidth + tabWidth <= availableWidth) {
          totalWidth += tabWidth
          visibleTabs++
        } else {
          break
        }
      }
      
      // Show/hide tabs and dropdown
      const hasOverflow = visibleTabs < tabItems.length
      
      if (hasOverflow) {
        wrapper.classList.add('has-overflow')
        // Hide tabs that don't fit and populate dropdown
        this.populateDropdown(tabItems.slice(visibleTabs))
        tabItems.forEach((tab, index) => {
          if (index >= visibleTabs) {
            tab.style.display = 'none'
          } else {
            tab.style.display = ''
          }
        })
      } else {
        wrapper.classList.remove('has-overflow')
        // Show all tabs and clear dropdown
        this.clearDropdown()
        tabItems.forEach(tab => {
          tab.style.display = ''
        })
      }
      
      // Always repopulate dropdown when screen size changes to ensure correct sorting
      if (hasOverflow) {
        this.populateDropdown(tabItems.slice(visibleTabs))
      }
      
      // Update building complex name display visibility
      this.updateBuildingComplexDisplay(hasOverflow, visibleTabs, tabItems)
    }
  }

  populateDropdown(hiddenTabs) {
    if (!this.hasDropdownMenuTarget || !this.hasDropdownTemplateTarget) return

    const dropdownMenu = this.dropdownMenuTarget
    const template = this.dropdownTemplateTarget
    dropdownMenu.innerHTML = ''

    // Set dynamic max height based on viewport height
    const viewportHeight = window.innerHeight
    const maxHeight = Math.min(viewportHeight * 0.7, 400) // Max 70% of viewport or 400px
    dropdownMenu.style.maxHeight = `${maxHeight}px`

    // Get all template items
    const templateItems = Array.from(template.content.querySelectorAll('li'))
    
    // Create array of items to add to dropdown with their names for sorting
    const dropdownItems = []
    
    // Find which template items correspond to hidden tabs
    hiddenTabs.forEach((hiddenTab, index) => {
      const link = hiddenTab.querySelector('a')
      if (link) {
        // Find corresponding template item by building complex id
        const buildingComplexId = link.dataset.buildingComplexId
        const templateItem = templateItems.find(item => {
          const templateLink = item.querySelector('a')
          return templateLink && templateLink.dataset.buildingComplexId === buildingComplexId
        })
        
        if (templateItem) {
          // Clone the template item and update active state
          const clonedItem = templateItem.cloneNode(true)
          const clonedLink = clonedItem.querySelector('a')
          if (link.classList.contains('active')) {
            clonedLink.classList.add('active')
          } else {
            clonedLink.classList.remove('active')
          }
          
          // Get the appropriate name for sorting based on screen size
          const buildingComplexId = clonedLink.dataset.buildingComplexId
          let sortName = ''
          
          if (buildingComplexId === 'all') {
            sortName = '0' // Put 'all' at the beginning
          } else {
            // Check if we're on mobile (screen width < 992px - Bootstrap lg breakpoint)
            const isMobile = window.innerWidth < 992
            
            if (isMobile) {
              // On mobile, sort by prefix (what the user actually sees)
              const prefixSpan = clonedLink.querySelector('span.d-inline.d-lg-none')
              sortName = prefixSpan ? prefixSpan.textContent.trim() : clonedLink.textContent.trim()
            } else {
              // On desktop, sort by full name
              const nameSpan = clonedLink.querySelector('span.d-none.d-lg-inline')
              sortName = nameSpan ? nameSpan.textContent.trim() : clonedLink.textContent.trim()
            }
          }
          
          dropdownItems.push({
            item: clonedItem,
            name: sortName
          })
        }
      }
    })
    
    // Sort by name alphabetically
    dropdownItems.sort((a, b) => a.name.localeCompare(b.name, 'hu'))
    
    // Append sorted items to dropdown
    dropdownItems.forEach(item => {
      dropdownMenu.appendChild(item.item)
    })
  }

  clearDropdown() {
    if (this.hasDropdownMenuTarget) {
      this.dropdownMenuTarget.innerHTML = ''
    }
  }

  updateBuildingComplexDisplay(hasOverflow, visibleTabs, tabItems) {
    // Check if we have a building display target
    if (!this.hasBuildingDisplayTarget) return

    const buildingDisplay = this.buildingDisplayTarget
    const currentBuildingId = buildingDisplay.dataset.buildingComplexId
    
    // Find the tab for the current building complex
    const currentTab = tabItems.find(tab => {
      const link = tab.querySelector('a')
      return link && link.dataset.buildingComplexId === currentBuildingId
    })

    if (currentTab) {
      const tabIndex = tabItems.indexOf(currentTab)
      const isVisible = !hasOverflow || tabIndex < visibleTabs
      
      // Show the building complex name only if the tab is hidden (in dropdown)
      if (isVisible) {
        buildingDisplay.style.display = 'none'
      } else {
        buildingDisplay.style.display = 'block'
      }
    }
  }
}
